import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DEFAULT_LANGUAGE_ID,
  LANGUAGE_OPTIONS,
  LANGUAGE_SESSION_KEY,
  LOW_CONFIDENCE_THRESHOLD,
  MAX_BATCH_IMAGES,
  MAX_PDF_PAGES,
  getLanguageOption,
} from '../lib/ocr/constants';
import { validateFile } from '../lib/ocr/validation';
import { decodeImageFile, releaseSource } from '../lib/ocr/preprocess';
import {
  getNextGenStatus,
  initNextGen,
  recognizeWith,
  routeEngine,
  terminateClassicWorker,
} from '../lib/ocr/engines';
import { hasUsableEmbeddedText, loadPdf, pageEmbeddedText, renderPdfPage } from '../lib/ocr/pdf';
import { joinPages } from '../lib/ocr/textUtils';
import { OcrError, toOcrError } from '../lib/ocr/types';
import { runSequential } from '../lib/ocr/queue';

let seq = 0;
const nextId = () => {
  seq += 1;
  return `item-${Date.now()}-${seq}`;
};

const VALID_LANGUAGE_IDS = LANGUAGE_OPTIONS.map((l) => l.id);

function readStoredLanguage() {
  try {
    const stored = sessionStorage.getItem(LANGUAGE_SESSION_KEY);
    if (stored && VALID_LANGUAGE_IDS.includes(stored)) return stored;
  } catch (e) {
    /* sessionStorage unavailable */
  }
  return DEFAULT_LANGUAGE_ID;
}

export function useExtractor() {
  const [languageId, setLanguageIdState] = useState(readStoredLanguage);
  const [requestedEngine, setRequestedEngine] = useState('classic');
  const [mode, setMode] = useState('document');
  const [items, setItems] = useState([]);
  const [validationMessages, setValidationMessages] = useState([]);
  const [isRunning, setIsRunning] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const [nextGenBlocker, setNextGenBlocker] = useState(null);
  const [nextGenReady, setNextGenReady] = useState(false);
  const [nextGenHidden, setNextGenHidden] = useState(false);

  const generationRef = useRef(0);
  const cancelRef = useRef(false);
  const itemsRef = useRef([]);
  const sourcesRef = useRef(new Map());

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  const routing = useMemo(() => routeEngine(requestedEngine, languageId), [requestedEngine, languageId]);
  const activeEngine = nextGenHidden && routing.engineId === 'nextgen' ? 'classic' : routing.engineId;

  const patchItem = useCallback((id, patch) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  }, []);

  const releaseItem = useCallback((item) => {
    if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
    const src = sourcesRef.current.get(item.id);
    if (src) {
      releaseSource(src);
      sourcesRef.current.delete(item.id);
    }
  }, []);

  /** Bumping the generation invalidates every in-flight run and clears stale errors. */
  const invalidateRuns = useCallback(() => {
    generationRef.current += 1;
    cancelRef.current = false;
    setIsRunning(false);
    setValidationMessages([]);
    setItems((prev) =>
      prev.map((it) =>
        it.status === 'processing' || it.status === 'preparing' || it.status === 'failed'
          ? { ...it, status: 'queued', progress: 0, statusText: '', error: null }
          : it,
      ),
    );
  }, []);

  const setLanguageId = useCallback(
    async (next) => {
      if (next === languageId) return;
      invalidateRuns();
      setLanguageIdState(next);
      try {
        sessionStorage.setItem(LANGUAGE_SESSION_KEY, next);
      } catch (e) {
        /* ignore */
      }
      // Never reuse a worker initialised for a different language configuration.
      await terminateClassicWorker();
      setAnnouncement(`Language set to ${getLanguageOption(next).label}. Recognition engine reset.`);
    },
    [languageId, invalidateRuns],
  );

  const selectEngine = useCallback(
    (engineId) => {
      if (engineId === 'nextgen' && languageId !== 'english') return;
      invalidateRuns();
      setRequestedEngine(engineId);
      setAnnouncement(`${engineId === 'nextgen' ? 'Next-Gen' : 'Classic'} OCR selected.`);
      if (engineId === 'nextgen') {
        setNextGenBlocker(null);
        initNextGen(({ text }) => setAnnouncement(text))
          .then(() => {
            setNextGenReady(true);
            setNextGenHidden(false);
            setAnnouncement('Next-Gen OCR ready.');
          })
          .catch((err) => {
            const e = toOcrError(err, 'ENGINE_UNAVAILABLE');
            setNextGenHidden(true);
            setNextGenReady(false);
            setRequestedEngine('classic');
            setNextGenBlocker({
              message: e.userMessage,
              technical: getNextGenStatus().blocker || e.message,
            });
          });
      }
    },
    [languageId, invalidateRuns],
  );

  const selectMode = useCallback(
    (nextMode) => {
      if (nextMode === mode) return;
      invalidateRuns();
      setMode(nextMode);
      setAnnouncement(
        `Recognition mode set to ${nextMode === 'poster' ? 'Poster / Mixed Layout' : 'Document / Screenshot'}.`,
      );
    },
    [mode, invalidateRuns],
  );

  const addFiles = useCallback(async (fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    const messages = [];
    const accepted = [];
    const currentImages = itemsRef.current.filter((i) => i.kind === 'image').length;
    let imageBudget = MAX_BATCH_IMAGES - currentImages;

    for (const file of incoming) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const validated = await validateFile(file);
        if (validated.kind === 'image' && imageBudget <= 0) {
          messages.push({
            level: 'error',
            text: `“${file.name}” was skipped. A batch can hold at most ${MAX_BATCH_IMAGES} images.`,
          });
          continue;
        }
        if (validated.kind === 'image') imageBudget -= 1;
        accepted.push({
          id: nextId(),
          file,
          name: file.name,
          sizeBytes: file.size,
          kind: validated.kind,
          width: validated.width || null,
          height: validated.height || null,
          previewUrl: validated.kind === 'image' ? URL.createObjectURL(file) : null,
          rotation: 0,
          crop: null,
          status: 'queued',
          progress: 0,
          statusText: '',
          result: null,
          editedText: null,
          error: null,
        });
      } catch (err) {
        const e = toOcrError(err, 'UNSUPPORTED_FORMAT');
        messages.push({ level: 'error', text: e.userMessage });
      }
    }

    if (accepted.length) {
      setItems((prev) => [...prev, ...accepted]);
      setAnnouncement(`${accepted.length} file${accepted.length > 1 ? 's' : ''} added to the queue.`);
    }
    setValidationMessages(messages);
  }, []);

  const removeItem = useCallback(
    (id) => {
      const target = itemsRef.current.find((i) => i.id === id);
      if (target) releaseItem(target);
      generationRef.current += 1;
      setItems((prev) => prev.filter((i) => i.id !== id));
      setAnnouncement('File removed from the queue.');
    },
    [releaseItem],
  );

  const clearAll = useCallback(() => {
    itemsRef.current.forEach(releaseItem);
    generationRef.current += 1;
    cancelRef.current = true;
    setItems([]);
    setValidationMessages([]);
    setIsRunning(false);
    setAnnouncement('Queue cleared.');
  }, [releaseItem]);

  const rotateItem = useCallback(
    (id, delta) => {
      const target = itemsRef.current.find((i) => i.id === id);
      if (!target) return;
      patchItem(id, { rotation: (((target.rotation + delta) % 360) + 360) % 360, error: null });
    },
    [patchItem],
  );

  const setCrop = useCallback(
    (id, crop) => {
      patchItem(id, { crop, error: null });
    },
    [patchItem],
  );

  const updateText = useCallback(
    (id, text) => {
      patchItem(id, { editedText: text });
    },
    [patchItem],
  );

  const getSource = useCallback(async (item) => {
    const cached = sourcesRef.current.get(item.id);
    if (cached) return cached;
    const decoded = await decodeImageFile(item.file);
    sourcesRef.current.set(item.id, decoded);
    return decoded;
  }, []);

  const runOne = useCallback(
    async (item, engineId, generation) => {
      const isStale = () => generationRef.current !== generation || cancelRef.current;
      const ctx = { languageId, mode, generation, isStale, pageNumber: null };
      const onStatus = ({ text, progress }) => {
        if (isStale()) return;
        patchItem(item.id, { statusText: text, progress: Math.min(0.99, progress) });
      };

      patchItem(item.id, { status: 'processing', progress: 0.01, statusText: 'Preparing', error: null });

      if (item.kind === 'pdf') {
        const loaded = await loadPdf(item.file);
        const pages = [];
        const failedPages = [];
        let firstResult = null;
        try {
          for (let p = 1; p <= loaded.pageCount; p += 1) {
            if (isStale()) throw new OcrError('CANCELLED', 'Recognition was cancelled.');
            let page = null;
            let rendered = null;
            try {
              // eslint-disable-next-line no-await-in-loop
              page = await loaded.doc.getPage(p);
              // eslint-disable-next-line no-await-in-loop
              const embedded = await pageEmbeddedText(page);
              if (hasUsableEmbeddedText(embedded)) {
                pages.push({ pageNumber: p, text: embedded, source: 'embedded', averageConfidence: null });
                onStatus({
                  text: `Page ${p} of ${loaded.pageCount} · embedded text`,
                  progress: p / loaded.pageCount,
                });
              } else {
                onStatus({
                  text: `Page ${p} of ${loaded.pageCount} · running OCR`,
                  progress: (p - 0.5) / loaded.pageCount,
                });
                // eslint-disable-next-line no-await-in-loop
                rendered = await renderPdfPage(page);
                // eslint-disable-next-line no-await-in-loop
                const res = await recognizeWith(
                  engineId,
                  { source: rendered.canvas, prepare: { rotationDeg: 0, crop: null } },
                  { ...ctx, pageNumber: p },
                  () => {},
                );
                if (!firstResult) firstResult = res;
                pages.push({
                  pageNumber: p,
                  text: res.text,
                  source: 'ocr',
                  averageConfidence: res.averageConfidence,
                  lines: res.lines,
                  tokens: res.tokens,
                  tables: res.tables,
                  lowResolution: res.lowResolution,
                });
                onStatus({ text: `Page ${p} of ${loaded.pageCount} done`, progress: p / loaded.pageCount });
              }
            } catch (pageErr) {
              const e = toOcrError(pageErr, 'PAGE_FAILED');
              if (e.code === 'CANCELLED' || e.code === 'STALE_RUN') throw e;
              failedPages.push({ pageNumber: p, message: e.userMessage });
            } finally {
              if (rendered) {
                rendered.canvas.width = 1;
                rendered.canvas.height = 1;
              }
              page?.cleanup?.();
            }
          }
        } finally {
          try {
            await loaded.doc.destroy();
          } catch (e) {
            /* ignore */
          }
        }

        if (isStale()) throw new OcrError('STALE_RUN', 'This run was superseded.');
        if (!pages.length) {
          throw new OcrError(
            'NO_TEXT_FOUND',
            'No readable text was found in this PDF. Every page failed or was blank.',
          );
        }
        const text = joinPages(pages);
        const confidences = pages.map((p) => p.averageConfidence).filter((c) => typeof c === 'number');
        const warnings = [];
        if (loaded.truncated) warnings.push(`Only the first ${MAX_PDF_PAGES} pages were processed.`);
        const embeddedCount = pages.filter((p) => p.source === 'embedded').length;
        if (embeddedCount) {
          warnings.push(`${embeddedCount} page(s) already contained selectable text and were read directly.`);
        }
        failedPages.forEach((f) => warnings.push(`Page ${f.pageNumber}: ${f.message}`));
        const lowResolution = pages.some((p) => p.lowResolution);
        if (lowResolution && firstResult) warnings.push(...firstResult.warnings.filter((w) => w.includes('too small')));

        const result = {
          text,
          lines: pages.flatMap((p) => p.lines || []),
          tokens: pages.flatMap((p) => p.tokens || []),
          tables: pages.flatMap((p) => p.tables || []),
          averageConfidence: confidences.length
            ? confidences.reduce((a, b) => a + b, 0) / confidences.length
            : null,
          engine: engineId,
          engineLabel: firstResult ? firstResult.engineLabel : 'Embedded PDF text (no OCR needed)',
          languageId,
          languageConfig: firstResult ? firstResult.languageConfig : getLanguageOption(languageId).tesseract,
          modelSource: firstResult ? firstResult.modelSource : 'pdf.js embedded text extraction',
          mode,
          preprocessVariant: firstResult ? firstResult.preprocessVariant : 'upscaled',
          segmentationMode: firstResult ? firstResult.segmentationMode : 'embedded text layer',
          durationMs: 0,
          pageNumber: null,
          invertedForDarkBackground: false,
          medianSourceTextHeight: firstResult ? firstResult.medianSourceTextHeight : null,
          lowResolution,
          warnings,
          pdfPages: pages.map((p) => ({ pageNumber: p.pageNumber, source: p.source })),
        };
        patchItem(item.id, {
          status: 'done',
          progress: 1,
          statusText: `${pages.length} page${pages.length > 1 ? 's' : ''} extracted`,
          result,
          editedText: text,
          error: null,
        });
        return;
      }

      const source = await getSource(item);
      if (isStale()) throw new OcrError('STALE_RUN', 'This run was superseded.');
      const result = await recognizeWith(
        engineId,
        { source, prepare: { rotationDeg: item.rotation, crop: item.crop } },
        ctx,
        onStatus,
      );
      if (isStale()) throw new OcrError('STALE_RUN', 'This run was superseded.');
      patchItem(item.id, {
        status: 'done',
        progress: 1,
        statusText: `Done in ${(result.durationMs / 1000).toFixed(1)}s`,
        result,
        editedText: result.text,
        error: null,
      });
    },
    [languageId, mode, patchItem, getSource],
  );

  const extract = useCallback(
    async (targetIds, engineOverride) => {
      const generation = generationRef.current + 1;
      generationRef.current = generation;
      cancelRef.current = false;
      setIsRunning(true);
      setValidationMessages([]);

      const targets = itemsRef.current.filter((i) => targetIds.includes(i.id));
      targets.forEach((t) =>
        patchItem(t.id, { status: 'queued', progress: 0, statusText: 'Waiting', error: null }),
      );
      setAnnouncement(`Starting extraction for ${targets.length} file${targets.length > 1 ? 's' : ''}.`);

      const engineId = engineOverride || activeEngine;
      await runSequential({
        items: targets,
        isCancelled: () => cancelRef.current || generationRef.current !== generation,
        stillPresent: (it) => itemsRef.current.some((cur) => cur.id === it.id),
        onCancelled: (it) =>
          patchItem(it.id, { status: 'queued', progress: 0, statusText: '', error: null }),
        process: async (it) => {
          try {
            await runOne(it, engineId, generation);
          } catch (err) {
            const e = toOcrError(err);
            if (e.code === 'STALE_RUN') {
              patchItem(it.id, { status: 'queued', progress: 0, statusText: '', error: null });
              return;
            }
            if (e.code === 'CANCELLED') {
              patchItem(it.id, { status: 'queued', progress: 0, statusText: 'Cancelled', error: null });
              return;
            }
            patchItem(it.id, {
              status: 'failed',
              progress: 0,
              statusText: '',
              error: {
                code: e.code,
                message: e.userMessage,
                engine: e.engine || engineId,
                technical: e.message !== e.userMessage ? e.message : null,
              },
            });
          }
        },
      });

      if (generationRef.current === generation) {
        setIsRunning(false);
        cancelRef.current = false;
        setAnnouncement('Extraction finished.');
      }
    },
    [activeEngine, patchItem, runOne],
  );

  const extractAll = useCallback(() => {
    const ids = itemsRef.current.map((i) => i.id);
    if (!ids.length) return Promise.resolve();
    return extract(ids);
  }, [extract]);

  const extractOne = useCallback((id) => extract([id]), [extract]);

  /** Explicit, user-triggered fallback after a Next-Gen failure. */
  const retryWithClassic = useCallback(
    (id) => {
      setRequestedEngine('classic');
      return extract([id], 'classic');
    },
    [extract],
  );

  const cancel = useCallback(() => {
    cancelRef.current = true;
    generationRef.current += 1;
    setIsRunning(false);
    setItems((prev) =>
      prev.map((it) =>
        it.status === 'processing' || it.status === 'preparing'
          ? { ...it, status: 'queued', progress: 0, statusText: 'Cancelled', error: null }
          : it,
      ),
    );
    setAnnouncement('Extraction cancelled.');
  }, []);

  useEffect(
    () => () => {
      itemsRef.current.forEach((item) => {
        if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
      });
      sourcesRef.current.forEach((src) => releaseSource(src));
      sourcesRef.current.clear();
    },
    [],
  );

  const successful = items.filter((i) => i.status === 'done');
  const copyAllText = successful
    .map((i) => `=== ${i.name} ===\n${i.editedText ?? i.result?.text ?? ''}`)
    .join('\n\n');

  return {
    languageId,
    setLanguageId,
    requestedEngine,
    activeEngine,
    selectEngine,
    routing,
    nextGenHidden,
    nextGenReady,
    nextGenBlocker,
    mode,
    selectMode,
    items,
    validationMessages,
    isRunning,
    announcement,
    addFiles,
    removeItem,
    clearAll,
    rotateItem,
    setCrop,
    updateText,
    extractAll,
    extractOne,
    retryWithClassic,
    cancel,
    successful,
    copyAllText,
    lowConfidenceThreshold: LOW_CONFIDENCE_THRESHOLD,
  };
}
